import type { Input } from '@gra/engine';

/** WASD/strzałki względem ekranu. Kamera patrzy wzdłuż osi Y świata, więc W = -y. */
export class KeyboardInput {
  private readonly down = new Set<string>();

  constructor(target: Window) {
    target.addEventListener('keydown', (e) => this.down.add(e.code));
    target.addEventListener('keyup', (e) => this.down.delete(e.code));
    target.addEventListener('blur', () => this.down.clear());
  }

  private any(...codes: string[]): number {
    return codes.some((c) => this.down.has(c)) ? 1 : 0;
  }

  moveDirection(): Input {
    const x = this.any('KeyD', 'ArrowRight') - this.any('KeyA', 'ArrowLeft');
    const y = this.any('KeyS', 'ArrowDown') - this.any('KeyW', 'ArrowUp');
    const len = Math.hypot(x, y);
    return len === 0 ? { moveX: 0, moveY: 0 } : { moveX: x / len, moveY: y / len };
  }
}

const STICK_RADIUS = 56;

/**
 * Wirtualny joystick na ekranach dotykowych: kładziesz palec gdziekolwiek na planszy,
 * tam pojawia się gałka; przesunięcie palca = kierunek i siła ruchu.
 */
export class TouchJoystick {
  private readonly base = document.createElement('div');
  private readonly knob = document.createElement('div');
  private pointer: number | null = null;
  private ox = 0;
  private oy = 0;
  private dir: Input = { moveX: 0, moveY: 0 };
  /** Joystick działa tylko w trakcie wyprawy. */
  enabled = false;

  constructor(surface: HTMLElement) {
    this.base.className = 'joystick';
    this.knob.className = 'joystick-knob';
    this.base.append(this.knob);
    this.base.hidden = true;
    document.getElementById('ui')!.append(this.base);

    surface.addEventListener('pointerdown', (e) => {
      if (!this.enabled || e.pointerType === 'mouse' || this.pointer !== null) return;
      this.pointer = e.pointerId;
      this.ox = e.clientX;
      this.oy = e.clientY;
      this.base.style.transform = `translate(${this.ox - STICK_RADIUS}px, ${this.oy - STICK_RADIUS}px)`;
      this.knob.style.transform = 'translate(0px, 0px)';
      this.base.hidden = false;
    });
    window.addEventListener('pointermove', (e) => {
      if (e.pointerId !== this.pointer) return;
      let dx = e.clientX - this.ox;
      let dy = e.clientY - this.oy;
      const len = Math.hypot(dx, dy);
      if (len > STICK_RADIUS) {
        dx = (dx / len) * STICK_RADIUS;
        dy = (dy / len) * STICK_RADIUS;
      }
      this.knob.style.transform = `translate(${dx}px, ${dy}px)`;
      // Martwa strefa przy środku, potem pełna prędkość już od ~60% wychylenia.
      const k = len < 8 ? 0 : Math.min(1, len / (STICK_RADIUS * 0.6));
      this.dir = len < 8 ? { moveX: 0, moveY: 0 } : { moveX: (dx / Math.hypot(dx, dy)) * k, moveY: (dy / Math.hypot(dx, dy)) * k };
    });
    const end = (e: PointerEvent) => {
      if (e.pointerId !== this.pointer) return;
      this.pointer = null;
      this.dir = { moveX: 0, moveY: 0 };
      this.base.hidden = true;
    };
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  }

  moveDirection(): Input {
    return this.dir;
  }
}

/** Klawiatura i joystick razem: wygrywa to, czego gracz właśnie używa. */
export class GameInput {
  constructor(
    private readonly keys: KeyboardInput,
    readonly touch: TouchJoystick,
  ) {}

  moveDirection(): Input {
    const k = this.keys.moveDirection();
    return k.moveX !== 0 || k.moveY !== 0 ? k : this.touch.moveDirection();
  }
}
